import { useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import TextInput from '@components/ui/Input/TextInput';
import { createUser } from '@/services/userService';
import { getRoles } from '@/services/roleService';
import { useTranslation } from '@/hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { CreatePlatformUserPayload } from '@/types/user';
import styles from './UserModals.module.scss';

// ============================================
// TYPES
// ============================================

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  roleIds: string[];
}

// ============================================
// DEFAULTS
// ============================================

const DEFAULT_VALUES: UserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  roleIds: [],
};

// ============================================
// COMPONENT
// ============================================

function CreateUserModal({ isOpen, onClose }: CreateUserModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<UserFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, reset]);

  // Fetch available roles for the picker (first page, all roles)
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles', 'pickerAll'],
    queryFn: () => getRoles({ page: 0, size: 100 }),
    enabled: isOpen,
  });

  const roleOptions = useMemo(
    () =>
      (rolesData?.content ?? []).map((role) => ({
        label: role.name,
        value: role.id,
      })),
    [rolesData],
  );

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreatePlatformUserPayload) => createUser(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      reset();
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      if (e.code === 41023) {
        addNotification({
          variant: 'critical',
          title: t('K_NOTIF_USER_DUPLICATE_EMAIL', 'A user with this email already exists'),
        });
      } else {
        addNotification({
          variant: 'critical',
          title: t(e.description, e.description),
        });
      }
    },
  });

  const onSubmit = useCallback(
    (data: UserFormValues) => {
      const payload: CreatePlatformUserPayload = {
        firstName: data.firstName,
        email: data.email,
      };
      if (data.lastName.trim()) {
        payload.lastName = data.lastName.trim();
      }
      if (data.roleIds.length > 0) {
        payload.roleIds = data.roleIds;
      }
      createMutation.mutate(payload);
    },
    [createMutation],
  );

  const handleClose = useCallback(() => {
    if (!createMutation.isPending) {
      reset();
      onClose();
    }
  }, [createMutation.isPending, reset, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_MODAL_CREATE_USER', 'Create User')}
      size="md"
      footer={
        <div className={styles.formFooter}>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button
            variant="filled"
            size="sm"
            type="submit"
            form="create-user-form"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending
              ? t('K_BUTTON_CREATING', 'Creating...')
              : t('K_CREATE', 'Create')}
          </Button>
        </div>
      }
    >
      <form
        id="create-user-form"
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
      >
        <div className={styles.row}>
          <TextInput
            label={t('K_LABEL_FIRST_NAME', 'First Name')}
            isRequired
            placeholder={t('K_PLACEHOLDER_FIRST_NAME', 'e.g. Jane')}
            error={errors.firstName?.message}
            {...register('firstName', {
              required: t('K_VALIDATION_FIRST_NAME_REQUIRED', 'First name is required'),
              maxLength: {
                value: 255,
                message: t('K_VALIDATION_MAX_255', 'Max 255 characters'),
              },
            })}
          />
          <TextInput
            label={t('K_LABEL_LAST_NAME', 'Last Name')}
            placeholder={t('K_PLACEHOLDER_LAST_NAME', 'e.g. Smith')}
            error={errors.lastName?.message}
            {...register('lastName', {
              maxLength: {
                value: 255,
                message: t('K_VALIDATION_MAX_255', 'Max 255 characters'),
              },
            })}
          />
        </div>

        <TextInput
          label={t('K_LABEL_EMAIL', 'Email')}
          isRequired
          placeholder={t('K_PLACEHOLDER_EMAIL', 'e.g. jane@company.com')}
          error={errors.email?.message}
          {...register('email', {
            required: t('K_VALIDATION_EMAIL_REQUIRED', 'Email is required'),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t('K_VALIDATION_EMAIL_INVALID', 'Please enter a valid email address'),
            },
          })}
        />

        <Controller
          name="roleIds"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label={t('K_LABEL_ROLES', 'Roles')}
              placeholder={t('K_PLACEHOLDER_SELECT_ROLES', 'Select roles')}
              options={roleOptions}
              value={field.value}
              onChange={field.onChange}
              loading={rolesLoading}
              searchable
            />
          )}
        />
      </form>
    </Modal>
  );
}

export default CreateUserModal;
